import type { ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { patientV2SoftBadgeClass, patientV2ExpandableCardDividerClass } from "../constants";
import { cn } from "@/lib/utils";

export type ExpandableRowCardDetail = {
  label: string;
  value?: string;
  renderValue?: ReactNode;
  fullWidth?: boolean;
  lineClamp?: number;
  wrap?: boolean;
};

type Props = {
  icon: ReactNode;
  title?: string;
  planTag?: string;
  subtitle?: ReactNode;
  headerMeta?: ReactNode;
  headerBadges?: ReactNode;
  details: ExpandableRowCardDetail[];
  actions?: ReactNode;
  defaultOpen?: boolean;
};

const headerControlClass =
  "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground";

function DetailField({
  label,
  value,
  renderValue,
  fullWidth,
  lineClamp,
  wrap,
}: ExpandableRowCardDetail) {
  return (
    <div
      className={cn(
        "min-w-0",
        fullWidth ? "w-full basis-full" : "w-full sm:basis-[calc(50%-0.75rem)] sm:max-w-[calc(50%-0.75rem)]",
      )}
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      {renderValue ? (
        <div className="mt-0.5">{renderValue}</div>
      ) : (
        <p
          className={cn(
            "mt-0.5 text-[14px] leading-snug text-foreground",
            lineClamp === 2 && "line-clamp-2",
            lineClamp === 3 && "line-clamp-3",
            wrap && "whitespace-normal break-words",
            !lineClamp && !wrap && "truncate",
          )}
        >
          {value || "—"}
        </p>
      )}
    </div>
  );
}

export function PatientV2ExpandableRowCard({
  icon,
  title,
  planTag,
  subtitle,
  headerMeta,
  headerBadges,
  details,
  actions,
  defaultOpen = false,
}: Props) {
  const hasDetails = details.length > 0;

  const headerBody = (
    <>
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#F3F4F6]">
        {icon}
      </div>
      <div className="min-w-0 flex-1 py-0.5">
        {subtitle ? (
          <div className={cn("min-w-0", planTag || title || headerBadges ? "mt-0.5" : "")}>
            {subtitle}
          </div>
        ) : null}
        {planTag || title || headerBadges ? (
          <div className="flex flex-wrap items-center gap-1.5">
            {planTag ? (
              <Badge className={`${patientV2SoftBadgeClass} uppercase`}>{planTag}</Badge>
            ) : (
              <p className="truncate text-[14px] font-medium leading-snug text-[#374151]">
                {title || "—"}
              </p>
            )}
            {headerBadges}
          </div>
        ) : null}
        {headerMeta ? <div className="mt-1 min-w-0">{headerMeta}</div> : null}
      </div>
    </>
  );

  return (
    <Collapsible
      defaultOpen={defaultOpen}
      className="group bg-white shadow-none"
    >
      <div className="flex items-center gap-1 px-4 py-3.5">
        {hasDetails ? (
          <CollapsibleTrigger
            className={cn(
              "flex min-w-0 flex-1 items-center gap-3 rounded-md text-left outline-none",
              "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            )}
          >
            {headerBody}
          </CollapsibleTrigger>
        ) : (
          <div className="flex min-w-0 flex-1 items-center gap-3 text-left">
            {headerBody}
          </div>
        )}

        <div
          className="flex shrink-0 items-center gap-0.5"
          onClick={(e) => e.stopPropagation()}
        >
          {actions ? (
            <div className="flex items-center [&_button]:h-8 [&_button]:w-8 [&_button]:p-0 [&_button:hover]:bg-transparent">
              {actions}
            </div>
          ) : null}
          {hasDetails ? (
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className={headerControlClass}
                aria-label="Toggle details"
              >
                <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
              </button>
            </CollapsibleTrigger>
          ) : null}
        </div>
      </div>

      {hasDetails ? (
        <CollapsibleContent
          className={cn(
            "overflow-hidden border-t bg-[#F3F4F6]",
            patientV2ExpandableCardDividerClass,
          )}
        >
          <div className="flex flex-wrap items-start gap-x-6 gap-y-4 px-4 py-4">
            {details.map((field) => (
              <DetailField
                key={field.label}
                label={field.label}
                value={field.value}
                renderValue={field.renderValue}
                fullWidth={field.fullWidth}
                lineClamp={field.lineClamp}
                wrap={field.wrap}
              />
            ))}
          </div>
        </CollapsibleContent>
      ) : null}
    </Collapsible>
  );
}
